import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';

import {
  apiSendRiskDetailsDraft,
  apiUpdateAutoQuotePolicyInfo,
  apiUpdateAutoQuoteVehicles,
  apiUpdateAutoQuoteDrivers,
  apiUpdatePropertyQuoteLocations,
  type Vehicle,
  type Driver,
  type Location,
} from 'api';

type PolicyInfoUpdate = Parameters<typeof apiUpdateAutoQuotePolicyInfo>[1];

const RISK_DETAILS_DRAFT_IDLE_MS = 1500;

interface StepConfig {
  apiFn: (quoteId: number, data: any) => Promise<any>;
  toRequestBody: (data: any) => unknown;
  invalidateKeys: string[];
  successMessage: string | ((activeStepKey: string) => string);
  errorMessage: string | ((activeStepKey: string) => string);
  aliasSteps?: string[];
}

const STEP_CONFIGS: Record<string, StepConfig> = {
  'policy-info': {
    apiFn: apiUpdateAutoQuotePolicyInfo,
    toRequestBody: (data) => data,
    invalidateKeys: ['policy-info', 'vehicle'],
    successMessage: 'Changes saved',
    errorMessage: 'Failed to save changes',
  },
  vehicle: {
    apiFn: apiUpdateAutoQuoteVehicles,
    toRequestBody: (vehicles) => ({ vehicles }),
    invalidateKeys: ['vehicle', 'assignment'],
    successMessage: (step) =>
      step === 'assignment' ? 'Assignments saved' : 'Vehicles saved',
    errorMessage: (step) =>
      step === 'assignment'
        ? 'Failed to save assignments'
        : 'Failed to save vehicles',
    aliasSteps: ['assignment'],
  },
  drivers: {
    apiFn: apiUpdateAutoQuoteDrivers,
    toRequestBody: (drivers) => ({ drivers }),
    invalidateKeys: ['drivers', 'assignment'],
    successMessage: 'Drivers saved',
    errorMessage: 'Failed to save drivers',
  },
  locations: {
    apiFn: apiUpdatePropertyQuoteLocations,
    toRequestBody: (locations) => ({ locations }),
    invalidateKeys: ['locations', 'general', 'additional'],
    successMessage: 'Locations saved',
    errorMessage: 'Failed to save locations',
    aliasSteps: ['general', 'additional'],
  },
};

interface UseQuoteFormStateOptions {
  quoteId: number;
  activeStepKey: string;
}

export const useQuoteFormState = ({
  quoteId,
  activeStepKey,
}: UseQuoteFormStateOptions) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const pendingDataRef = useRef<Record<string, any>>({});
  const draftTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [quotesWereReset, setQuotesWereReset] = useState(false);

  useEffect(() => {
    if (activeStepKey === 'carriers') {
      setQuotesWereReset(false);
    }
  }, [activeStepKey]);

  useEffect(() => () => clearTimeout(draftTimerRef.current), []);

  const getPendingStepKeys = useCallback(
    () =>
      Object.keys(pendingDataRef.current).filter((key) => {
        const data = pendingDataRef.current[key];
        if (!data) return false;
        if (key === 'policy-info' && Object.keys(data).length === 0)
          return false;
        return true;
      }),
    [],
  );

  const scheduleRiskDetailsDraft = useCallback(() => {
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      const steps = Object.fromEntries(
        getPendingStepKeys().map((key) => [
          key,
          STEP_CONFIGS[key].toRequestBody(pendingDataRef.current[key]),
        ]),
      );
      if (Object.keys(steps).length === 0) return;
      apiSendRiskDetailsDraft(quoteId, steps).catch(() => undefined);
    }, RISK_DETAILS_DRAFT_IDLE_MS);
  }, [quoteId, getPendingStepKeys]);

  const showSuccessToast = useCallback(
    (message: string) => {
      toast({
        title: message,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [toast],
  );

  const showErrorToast = useCallback(
    (message: string) => {
      toast({
        title: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [toast],
  );

  const handleQuotesReset = useCallback(() => {
    setQuotesWereReset(true);
    queryClient.invalidateQueries({
      queryKey: ['marketplace-quote', quoteId, 'carriers'],
    });
    queryClient.invalidateQueries({
      queryKey: ['marketplace-quote', quoteId, 'carriers-state'],
    });
  }, [queryClient, quoteId]);

  const createMutationConfig = useCallback(
    (configKey: string) => {
      const config = STEP_CONFIGS[configKey];
      if (!config) return null;

      return {
        mutationFn: (data: any) => config.apiFn(quoteId, data),
        onSuccess: (response: { quotes_reset?: boolean }) => {
          config.invalidateKeys.forEach((key) => {
            queryClient.invalidateQueries({
              queryKey: ['marketplace-quote', quoteId, key],
            });
          });

          pendingDataRef.current[configKey] = null;
          setHasPendingChanges(false);

          if (response?.quotes_reset) {
            handleQuotesReset();
          }

          const message =
            typeof config.successMessage === 'function'
              ? config.successMessage(activeStepKey)
              : config.successMessage;
          showSuccessToast(message);
        },
        onError: (error: any) => {
          const backendMessage = error?.error || error?.detail;
          const fallbackMessage =
            typeof config.errorMessage === 'function'
              ? config.errorMessage(activeStepKey)
              : config.errorMessage;
          showErrorToast(backendMessage || fallbackMessage);
        },
      };
    },
    [
      quoteId,
      activeStepKey,
      queryClient,
      handleQuotesReset,
      showSuccessToast,
      showErrorToast,
    ],
  );

  const policyInfoMutation = useMutation(createMutationConfig('policy-info')!);
  const vehicleMutation = useMutation(createMutationConfig('vehicle')!);
  const driversMutation = useMutation(createMutationConfig('drivers')!);
  const locationsMutation = useMutation(createMutationConfig('locations')!);

  const mutations: Record<string, ReturnType<typeof useMutation>> = useMemo(
    () => ({
      'policy-info': policyInfoMutation,
      vehicle: vehicleMutation,
      drivers: driversMutation,
      locations: locationsMutation,
    }),
    [policyInfoMutation, vehicleMutation, driversMutation, locationsMutation],
  );

  const createUpdateHandler = useCallback(
    <T>(configKey: string) =>
      (data: T) => {
        pendingDataRef.current[configKey] = data;
        queueMicrotask(() => setHasPendingChanges(true));
        scheduleRiskDetailsDraft();
      },
    [scheduleRiskDetailsDraft],
  );

  const handlePolicyInfoUpdate = useCallback(
    (data: PolicyInfoUpdate) => {
      pendingDataRef.current['policy-info'] = {
        ...pendingDataRef.current['policy-info'],
        ...data,
      };
      queueMicrotask(() => setHasPendingChanges(true));
      scheduleRiskDetailsDraft();
    },
    [scheduleRiskDetailsDraft],
  );

  const handleVehiclesUpdate = useMemo(
    () => createUpdateHandler<Vehicle[]>('vehicle'),
    [createUpdateHandler],
  );

  const handleDriversUpdate = useMemo(
    () => createUpdateHandler<Driver[]>('drivers'),
    [createUpdateHandler],
  );

  const handleLocationsUpdate = useMemo(
    () => createUpdateHandler<Location[]>('locations'),
    [createUpdateHandler],
  );

  const handleSave = useCallback(async () => {
    clearTimeout(draftTimerRef.current);
    const pendingKeys = getPendingStepKeys();

    if (pendingKeys.length === 0) return;

    const saveOrder = ['drivers', 'vehicle', 'policy-info', 'locations'];
    const orderedKeys = pendingKeys.sort(
      (a, b) => saveOrder.indexOf(a) - saveOrder.indexOf(b),
    );

    for (const configKey of orderedKeys) {
      const pendingData = pendingDataRef.current[configKey];
      const mutation = mutations[configKey];
      if (mutation && pendingData) {
        try {
          await mutation.mutateAsync(pendingData);
        } catch {
          return;
        }
      }
    }
  }, [mutations, getPendingStepKeys]);

  const isSaving = Object.values(mutations).some((m) => m.isPending);

  const dismissQuotesResetBanner = useCallback(() => {
    setQuotesWereReset(false);
  }, []);

  const getPendingVehicles = useCallback(
    (): Vehicle[] | null => pendingDataRef.current['vehicle'] ?? null,
    [],
  );

  return {
    hasPendingChanges,
    isSaving,
    quotesWereReset,
    dismissQuotesResetBanner,
    handlePolicyInfoUpdate,
    handleVehiclesUpdate,
    handleDriversUpdate,
    handleLocationsUpdate,
    handleSave,
    getPendingVehicles,
    updatePolicyInfoMutation: policyInfoMutation,
    updateVehiclesMutation: vehicleMutation,
    updateDriversMutation: driversMutation,
    updateLocationsMutation: locationsMutation,
  };
};
